import DOMPurify from "dompurify";
import { api } from "./api";

export type EmailPdfInput = {
  subject: string;
  from: string;
  to: string;
  cc?: string;
  bcc?: string;
  date: string | null;
  html: string | null;
  text: string | null;
  accountEmail?: string;
  accountId?: string;
  folder?: string;
  uid?: number;
  attachments?: Array<{
    partId: string;
    contentType: string;
    contentId?: string | null;
    filename?: string;
  }>;
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function sanitizeFilename(name: string): string {
  const cleaned = name
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
  return cleaned || "email";
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("FileReader failed"));
    reader.readAsDataURL(blob);
  });
}

async function fetchAsDataUrl(url: string): Promise<string | null> {
  try {
    // Same-origin /api needs Bearer (credentials alone are not enough)
    const headers: Record<string, string> = {};
    if (url.includes("/api/")) {
      const token =
        sessionStorage.getItem("mailstorm_session_token") ||
        localStorage.getItem("mailstorm_session_token");
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    const res = await fetch(url, { credentials: "include", headers });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.size) return null;
    return await blobToDataUrl(blob);
  } catch {
    return null;
  }
}

/** Prefer authenticated attachmentBlob for rewritten cid → /api/…/attachments/… URLs. */
async function resolveImgToDataUrl(
  rawSrc: string,
  mail: EmailPdfInput,
  cidToPart: Map<string, string>,
): Promise<string | null> {
  if (!rawSrc || rawSrc.startsWith("data:")) return null;

  if (/^cid:/i.test(rawSrc)) {
    const cid = rawSrc.replace(/^cid:/i, "").replace(/^<|>$/g, "").toLowerCase();
    const partId = cidToPart.get(cid);
    if (partId && mail.accountId && mail.folder && mail.uid != null) {
      try {
        const { blob } = await api.attachmentBlob(mail.accountId, mail.folder, mail.uid, partId);
        return await blobToDataUrl(blob);
      } catch {
        return null;
      }
    }
    return null;
  }

  // /api/mail/messages/:uid/attachments/:partId?...
  const attMatch = rawSrc.match(
    /\/api\/mail\/messages\/(\d+)\/attachments\/([^/?#]+)/i,
  );
  if (attMatch) {
    let accountId = mail.accountId;
    let folder = mail.folder;
    try {
      const u = new URL(rawSrc, window.location.origin);
      accountId = u.searchParams.get("accountId") || accountId;
      folder = u.searchParams.get("folder") || folder;
    } catch {
      /* keep mail.* */
    }
    const uid = Number(attMatch[1]);
    const partId = decodeURIComponent(attMatch[2]);
    if (accountId && folder && Number.isFinite(uid)) {
      try {
        const { blob } = await api.attachmentBlob(accountId, folder, uid, partId);
        return await blobToDataUrl(blob);
      } catch {
        /* fall through to fetch */
      }
    }
  }

  if (rawSrc.startsWith("/api/") || /^https?:\/\//i.test(rawSrc)) {
    const abs = rawSrc.startsWith("/api/")
      ? `${window.location.origin}${rawSrc}`
      : rawSrc;
    return fetchAsDataUrl(abs);
  }

  return null;
}

/**
 * Inline every <img> (cid:, /api/… attachments, http/https) as data: URLs
 * so html2canvas/PDF can embed logos without CORS failures.
 */
async function inlineImagesForPdf(
  html: string,
  mail: EmailPdfInput,
): Promise<string> {
  const doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, "text/html");
  const root = doc.getElementById("root");
  if (!root) return html;

  const cidToPart = new Map<string, string>();
  for (const att of mail.attachments || []) {
    if (!att.contentId) continue;
    if (!att.contentType.startsWith("image/") && !/\.(png|jpe?g|gif|webp|svg)$/i.test(att.filename || "")) {
      // still allow if contentId present — many logos are image/*
    }
    cidToPart.set(att.contentId.toLowerCase(), att.partId);
  }

  const imgs = Array.from(root.querySelectorAll("img"));
  await Promise.all(
    imgs.map(async (img) => {
      const rawSrc = (img.getAttribute("src") || "").trim();
      const dataUrl = await resolveImgToDataUrl(rawSrc, mail, cidToPart);
      if (dataUrl) img.setAttribute("src", dataUrl);
    }),
  );

  // Also rewrite url(cid:…) in inline style attributes
  for (const el of Array.from(root.querySelectorAll("[style]"))) {
    const style = el.getAttribute("style") || "";
    if (!/cid:/i.test(style)) continue;
    let next = style;
    const matches = style.matchAll(/url\(\s*['"]?cid:([^)'"\s]+)['"]?\s*\)/gi);
    for (const m of matches) {
      const cid = String(m[1] || "")
        .replace(/^<|>$/g, "")
        .toLowerCase();
      const partId = cidToPart.get(cid);
      if (!partId || !mail.accountId || !mail.folder || mail.uid == null) continue;
      try {
        const { blob } = await api.attachmentBlob(mail.accountId, mail.folder, mail.uid, partId);
        const dataUrl = await blobToDataUrl(blob);
        next = next.replace(m[0], `url("${dataUrl}")`);
      } catch {
        /* keep original */
      }
    }
    if (next !== style) el.setAttribute("style", next);
  }

  return root.innerHTML;
}

/** Build a print/PDF-ready HTML document: email body only (with inlined images). */
export async function buildEmailPrintDocument(
  mail: EmailPdfInput,
): Promise<{ html: string; filename: string }> {
  const subject = mail.subject?.trim() || "(senza oggetto)";
  const dayStamp = mail.date
    ? new Date(mail.date).toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10);
  const filename = `${sanitizeFilename(subject)}_${dayStamp}.pdf`;

  let bodyHtml: string;
  if (mail.html) {
    // Inline images first (cid:/api/) so DOMPurify keeps data: URLs, not broken cid refs
    const withImages = await inlineImagesForPdf(mail.html, mail);
    bodyHtml = DOMPurify.sanitize(withImages, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
      ADD_DATA_URI_TAGS: ["img"],
      ADD_ATTR: ["style", "bgcolor", "background", "width", "height", "align", "valign", "border", "cellpadding", "cellspacing"],
    });
  } else {
    bodyHtml = `<pre style="white-space:pre-wrap;font-family:ui-sans-serif,system-ui,sans-serif;font-size:13px;line-height:1.5;margin:0">${escapeHtml(
      mail.text || "(vuoto)",
    )}</pre>`;
  }

  const html = `<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(subject)}</title>
  <style>
    @page { margin: 0; }
    html, body { margin: 0; padding: 0; }
    img { max-width: 100% !important; height: auto !important; }
    table { max-width: 100% !important; }
  </style>
</head>
<body id="mailstorm-pdf-root">${bodyHtml}</body>
</html>`;

  return { html, filename };
}

/** Render email as PDF (full HTML + images) and open/download in the browser. */
export async function downloadEmailPdf(mail: EmailPdfInput): Promise<{ filename: string }> {
  const { html, filename } = await buildEmailPrintDocument(mail);

  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  host.style.cssText =
    "position:fixed;left:-10000px;top:0;width:820px;z-index:-1;opacity:0;pointer-events:none;";
  host.innerHTML = html;
  document.body.appendChild(host);

  // Wait for inlined/data images to decode before canvas capture
  const imgs = Array.from(host.querySelectorAll("img"));
  await Promise.all(
    imgs.map(
      (img) =>
        img.complete
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              img.onload = () => resolve();
              img.onerror = () => resolve();
              window.setTimeout(() => resolve(), 4000);
            }),
    ),
  );

  const root = (host.querySelector("#mailstorm-pdf-root") as HTMLElement | null) || host;
  try {
    const html2pdf = (await import("html2pdf.js")).default;
    const worker = html2pdf()
      .set({
        margin: 0,
        filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: null,
          logging: false,
          windowWidth: 820,
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      } as never)
      .from(root);

    const pdfBlob = (await worker.outputPdf("blob")) as Blob;
    const url = URL.createObjectURL(pdfBlob);

    window.open(url, "_blank", "noopener,noreferrer");
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
    return { filename };
  } finally {
    host.remove();
  }
}
